import React, { useState, useMemo } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectVendors({ project, updateProject, safeNavigate }) {
  const [newVendor, setNewVendor] = useState({ name: '', category: 'Venue', status: 'Inquiry', email: '', phone: '', notes: '' });
  
  const vendors = project.vendors || [];

  const categories = [
    'Venue', 'Catering', 'Photography', 'Videography', 'Attire', 'Hair & Makeup',
    'Florals', 'Music/DJ', 'Rentals', 'Stationery', 'Favors', 'Officiant', 'Planner', 'Other'
  ];

  const statuses = ['Inquiry', 'Interviewing', 'Booked', 'Passed'];

  const stats = useMemo(() => {
    return {
      total: vendors.length,
      booked: vendors.filter(v => v.status === 'Booked' || v.status.includes('Host-it!')).length,
      inquiries: vendors.filter(v => v.status === 'Inquiry' || v.status === 'Interviewing').length,
    };
  }, [vendors]);

  const handleAddVendor = (e) => {
    e.preventDefault();
    if (!newVendor.name.trim()) return;

    updateProject(project.id, {
      vendors: [...vendors, { ...newVendor, id: Date.now().toString() }]
    });
    
    setNewVendor({ name: '', category: 'Venue', status: 'Inquiry', email: '', phone: '', notes: '' });
  };

  const handleRemoveVendor = (id) => {
    if (window.confirm("Remove this vendor from the project?")) {
      updateProject(project.id, {
        vendors: vendors.filter(v => v.id !== id)
      });
    }
  };

  const handleUpdateVendor = (id, field, value) => {
    const updatedVendors = vendors.map(v => v.id === id ? { ...v, [field]: value } : v);
    updateProject(project.id, { vendors: updatedVendors });
  };

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in flex flex-col h-full">
        {/* Header & Stats */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-6">
                <div>
                    <h2 className="text-2xl font-bold font-serif text-gray-900">Vendor Team</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage inquiries, track hired professionals, and store contact details.</p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button 
                        onClick={() => safeNavigate('listings')} 
                        className="bg-gray-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-bold transition shadow-sm flex items-center justify-center gap-2 flex-1 sm:flex-none whitespace-nowrap"
                    >
                        <PlannerIcons.Briefcase /> Browse Vendors
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total Tracked</p>
                    <p className="text-2xl font-black text-gray-900">{stats.total}</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-1">Hired / Booked</p>
                    <p className="text-2xl font-black text-emerald-700">{stats.booked}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">Active Inquiries</p>
                    <p className="text-2xl font-black text-blue-700">{stats.inquiries}</p>
                </div>
            </div>
        </div>

        {/* Add Vendor Form */}
        <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Add External Vendor</h3>
            <form onSubmit={handleAddVendor} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
                <input 
                    type="text" 
                    required
                    value={newVendor.name} 
                    onChange={e => setNewVendor({...newVendor, name: e.target.value})} 
                    placeholder="Vendor / Business Name" 
                    className="lg:col-span-2 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                />
                <select 
                    value={newVendor.category} 
                    onChange={e => setNewVendor({...newVendor, category: e.target.value})} 
                    className="lg:col-span-1 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                >
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select 
                    value={newVendor.status} 
                    onChange={e => setNewVendor({...newVendor, status: e.target.value})} 
                    className="lg:col-span-1 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                >
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input 
                    type="email" 
                    value={newVendor.email} 
                    onChange={e => setNewVendor({...newVendor, email: e.target.value})} 
                    placeholder="Email Address" 
                    className="lg:col-span-1 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                />
                <input 
                    type="tel" 
                    value={newVendor.phone} 
                    onChange={e => setNewVendor({...newVendor, phone: e.target.value})} 
                    placeholder="Phone Number" 
                    className="lg:col-span-1 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50"
                />
                <button 
                    type="submit" 
                    disabled={!newVendor.name.trim()}
                    className="bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-lg font-bold transition shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                >
                    <PlannerIcons.Plus /> Add
                </button>
            </form>
        </div>

        {/* Vendor Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 flex-1 items-start">
            {vendors.map((vendor) => (
                <div key={vendor.id} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm relative group hover:border-pink-300 transition-colors flex flex-col h-full">
                    <div className="absolute top-4 right-4 flex gap-2">
                        {vendor.id.length > 15 && vendor.status.includes('Host-it!') && safeNavigate && (
                            <button
                                onClick={() => safeNavigate('vendorProfile', { vendorId: vendor.id })}
                                className="p-1.5 text-pink-600 hover:bg-pink-50 rounded-lg transition"
                                title="View Host-it! Profile"
                            >
                                <PlannerIcons.ExternalLink />
                            </button>
                        )}
                        <button
                            onClick={() => handleRemoveVendor(vendor.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 bg-gray-50 hover:bg-red-50 rounded-lg transition opacity-0 group-hover:opacity-100"
                            title="Remove Vendor"
                        >
                            <PlannerIcons.Trash />
                        </button>
                    </div>

                    <div className="mb-4 pr-16">
                        <input 
                            type="text" 
                            value={vendor.name} 
                            onChange={(e) => handleUpdateVendor(vendor.id, 'name', e.target.value)} 
                            className="font-bold text-gray-900 text-lg outline-none w-full border-b border-transparent focus:border-gray-200 hover:bg-gray-50 transition-colors px-1 -ml-1 rounded"
                        />
                        <div className="flex gap-2 mt-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600 bg-gray-100 px-2 py-0.5 rounded-md">
                                {vendor.category}
                            </span>
                            <select
                                value={vendor.status}
                                onChange={(e) => handleUpdateVendor(vendor.id, 'status', e.target.value)}
                                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md outline-none cursor-pointer appearance-none ${
                                    vendor.status === 'Booked' || vendor.status.includes('Host-it!') ? 'bg-emerald-100 text-emerald-700' :
                                    vendor.status === 'Passed' ? 'bg-gray-200 text-gray-600' :
                                    'bg-blue-100 text-blue-700'
                                }`}
                            >
                                {vendor.status.includes('Host-it!') && <option value={vendor.status}>{vendor.status}</option>}
                                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    
                    <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="text-gray-400">✉️</span>
                            <input 
                                type="email" 
                                value={vendor.email || ''} 
                                onChange={(e) => handleUpdateVendor(vendor.id, 'email', e.target.value)} 
                                placeholder="Add email..."
                                className="outline-none w-full border-b border-transparent focus:border-gray-200 hover:bg-gray-50 transition-colors px-1 -ml-1 rounded"
                            />
                            {vendor.email && (
                                <a href={`mailto:${vendor.email}`} className="text-gray-400 hover:text-pink-600 transition p-1">
                                    <PlannerIcons.ExternalLink />
                                </a>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="text-gray-400">📞</span>
                            <input 
                                type="tel" 
                                value={vendor.phone || ''} 
                                onChange={(e) => handleUpdateVendor(vendor.id, 'phone', e.target.value)} 
                                placeholder="Add phone..."
                                className="outline-none w-full border-b border-transparent focus:border-gray-200 hover:bg-gray-50 transition-colors px-1 -ml-1 rounded"
                            />
                            {vendor.phone && (
                                <a href={`tel:${vendor.phone}`} className="text-gray-400 hover:text-pink-600 transition p-1">
                                    <PlannerIcons.ExternalLink />
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <textarea 
                            value={vendor.notes || ''} 
                            onChange={(e) => handleUpdateVendor(vendor.id, 'notes', e.target.value)} 
                            placeholder="Add meeting notes, links, or contract details here..."
                            className="w-full text-xs text-gray-500 bg-gray-50 p-3 rounded-lg leading-relaxed outline-none focus:ring-1 focus:ring-pink-500 resize-none h-24 custom-scrollbar"
                        />
                    </div>
                </div>
            ))}
            
            {vendors.length === 0 && (
                <div className="col-span-full py-16 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                    <div className="text-4xl mb-3">💼</div>
                    <p className="font-medium text-gray-500">Your vendor team is empty.</p>
                    <p className="text-sm mt-1">Add professionals to track who you've hired and who you're interviewing.</p>
                </div>
            )}
        </div>
    </div>
  );
}